import { useEffect, useRef, useState } from "react"

import { cn } from "@/domains/library"

export interface AnimatedNumberProps {
  /** Display string that may contain a number, e.g. "$142.06M", "24.8%", or a non-numeric label like "Live". */
  readonly value: string
  /** Count-up duration in milliseconds. */
  readonly durationMs?: number
  /** Delay before the count-up starts, used to stagger a group of numbers. */
  readonly delayMs?: number
  readonly className?: string
}

interface ParsedValue {
  readonly prefix: string
  readonly suffix: string
  readonly amount: number
  readonly decimals: number
  readonly useGrouping: boolean
}

/** Captures an optional non-digit prefix ($, ~), the number itself (commas/decimals), and a trailing suffix (M, %, /vote). */
const NUMERIC_PATTERN = /^(\D*?)([\d,]*\.?\d+)(.*)$/s

function parseValue(value: string): ParsedValue | null {
  const match = NUMERIC_PATTERN.exec(value)
  if (match == null) return null
  const [, prefix, numeric, suffix] = match
  const decimalPart = numeric.split(".")[1]
  return {
    prefix,
    suffix,
    amount: Number.parseFloat(numeric.replace(/,/g, "")),
    decimals: decimalPart?.length ?? 0,
    useGrouping: numeric.includes(","),
  }
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
}

/** easeOutExpo: quick start, slow settle — reads as data snapping into place. */
const easeOutExpo = (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t))

/**
 * Animates the numeric part of a display string, counting up from zero on first
 * arrival and from the previous value on later updates, then pulses to signal a
 * live change. Non-numeric values render immediately. Purely presentational and
 * Storybook-safe; respects prefers-reduced-motion by snapping to the final value.
 */
export function AnimatedNumber({ value, durationMs = 1400, delayMs = 0, className }: AnimatedNumberProps) {
  const [text, setText] = useState(value)
  const [isPulsing, setIsPulsing] = useState(false)
  const currentAmountRef = useRef<number | null>(null)

  useEffect(() => {
    const parsed = parseValue(value)

    if (parsed == null) {
      currentAmountRef.current = null
      setText(value)
      return
    }

    const format = (amount: number): string =>
      `${parsed.prefix}${amount.toLocaleString("en-US", {
        minimumFractionDigits: parsed.decimals,
        maximumFractionDigits: parsed.decimals,
        useGrouping: parsed.useGrouping,
      })}${parsed.suffix}`

    const from = currentAmountRef.current
    const to = parsed.amount

    if (from === to) {
      setText(format(to))
      return
    }

    const isLiveUpdate = from != null

    if (prefersReducedMotion()) {
      currentAmountRef.current = to
      setText(format(to))
      if (isLiveUpdate) setIsPulsing(true)
      return
    }

    const start = from ?? 0
    let frameId = 0
    let startTime: number | null = null

    const step = (now: number) => {
      if (startTime == null) startTime = now
      const progress = Math.min((now - startTime) / durationMs, 1)
      const amount = start + (to - start) * easeOutExpo(progress)
      currentAmountRef.current = amount
      setText(format(amount))
      if (progress < 1) {
        frameId = requestAnimationFrame(step)
      } else {
        currentAmountRef.current = to
        if (isLiveUpdate) setIsPulsing(true)
      }
    }

    const timeoutId = window.setTimeout(() => {
      frameId = requestAnimationFrame(step)
    }, delayMs)

    return () => {
      window.clearTimeout(timeoutId)
      if (frameId !== 0) cancelAnimationFrame(frameId)
    }
  }, [value, durationMs, delayMs])

  return (
    <span
      className={cn("animated-number tabular-nums", isPulsing && "animated-number--pulse", className)}
      onAnimationEnd={() => setIsPulsing(false)}
    >
      {text}
    </span>
  )
}
